import React, { useState, useEffect, useRef } from 'react';
import { Send, User, MoreVertical, ArrowLeft, Phone, Video } from 'lucide-react';

export default function MessageThread({
  threadId,
  recipientId,
  currentUserId,
  recipientName,
  setActiveSection,
  triggerInboxRefresh
}) {
  // Get current user ID from localStorage if not provided
  const getCurrentUserId = () => {
    if (currentUserId) return currentUserId;
    
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        return user.id || user.user_id || user.userId;
      } catch (e) {
        console.warn('Failed to parse user from localStorage');
      }
    }
    
    const userId = localStorage.getItem('userId') || localStorage.getItem('currentUserId');
    return userId ? parseInt(userId, 10) : null;
  };

  const actualCurrentUserId = getCurrentUserId();
  
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [activeMessageMenu, setActiveMessageMenu] = useState(null);
  const [recipientInfo, setRecipientInfo] = useState(null);
  const scrollRef = useRef(null);

  // Validation effect to check if all required props are provided
  useEffect(() => {
    if (!threadId) {
      console.error('Thread ID is missing');
      setError('Thread ID is required');
      setLoading(false);
      return;
    }
    if (!actualCurrentUserId) {
      console.error('Current user ID is missing');
      setError('Current user ID not found. Please try logging in again.');
      setLoading(false);
      return;
    }
    // Clear error if all required props are present
    setError(null);
  }, [threadId, actualCurrentUserId]);

  // Fetch messages
  useEffect(() => {
    const fetchMessages = async () => {
      // Don't fetch if we have validation errors
      if (!threadId || !actualCurrentUserId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found');
        }

        // Use the correct API endpoint from our backend
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/messages/thread/${threadId}/${actualCurrentUserId}`,
          { 
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            credentials: 'include' 
          }
        );
        
        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          console.error('Fetch error response:', errorData);
          throw new Error(errorData.error || `Failed to fetch messages: ${res.status} ${res.statusText}`);
        }
        
        const data = await res.json();
        setMessages(Array.isArray(data) ? data : []);
        
        // Extract recipient info from messages if not provided
        if (!recipientName && data.length > 0) {
          const firstMessage = data[0];
          if (firstMessage.message_from !== actualCurrentUserId) {
            setRecipientInfo({
              name: `${firstMessage.sender_firstName} ${firstMessage.sender_lastName}`,
              roleId: firstMessage.sender_roleId
            });
          } else if (firstMessage.message_to !== actualCurrentUserId) {
            setRecipientInfo({
              name: `${firstMessage.recipient_firstName} ${firstMessage.recipient_lastName}`,
              roleId: firstMessage.recipient_roleId
            });
          }
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching messages:', err);
        setError(err.message);
        setMessages([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [threadId, actualCurrentUserId, recipientName]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (scrollRef.current && messages.length > 0) {
      setTimeout(() => {
        scrollRef.current.scrollTo({
          top: scrollRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }, 100);
    }
  }, [messages]);

  const handleSend = async () => {
    if (!threadId) {
      setError('Thread ID is required');
      return;
    }
    if (!actualCurrentUserId) {
      setError('Current user ID not found. Please try logging in again.');
      return;
    }
    
    const messageText = newMessage.trim();
    
    try {
      setIsSending(true);
      setError(null);
      
      // Determine recipient ID from messages if not provided
      let targetRecipientId = recipientId;
      if (!targetRecipientId && messages.length > 0) {
        const firstMessage = messages[0];
        if (firstMessage.message_from !== actualCurrentUserId) {
          targetRecipientId = firstMessage.message_from;
        } else {
          targetRecipientId = firstMessage.message_to;
        }
      }
      
      if (!targetRecipientId) {
        throw new Error('Unable to determine recipient ID');
      }
      
      // Use the send-reply endpoint for existing threads
      const url = `${process.env.REACT_APP_BACKEND_URL}/messages/send-reply`;
      const payload = {
        thread_id: parseInt(threadId),
        message_from: parseInt(actualCurrentUserId),
        message_to: parseInt(targetRecipientId),
        message: messageText
      };

      // Clear input immediately for better UX
      setNewMessage('');

      // Add optimistic message
      const optimisticMessage = {
        id: `temp-${Date.now()}`,
        thread_id: threadId,
        message_from: actualCurrentUserId,
        message_to: targetRecipientId,
        message: messageText,
        created_at: new Date().toISOString(),
        isOptimistic: true,
        sender_firstName: 'You',
        sender_lastName: '',
        sender_roleId: '3'
      };
      
      setMessages(prev => [...prev, optimisticMessage]);

      const response = await fetch(url, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { message: `HTTP ${response.status} - ${response.statusText}` };
        }
        throw new Error(errorData.error || errorData.message || 'Failed to send message');
      }

      const result = await response.json();

      // Refresh messages from server to get the real message
      try {
        const refreshRes = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/messages/thread/${threadId}/${actualCurrentUserId}`,
          { 
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            },
            credentials: 'include' 
          }
        );
        if (refreshRes.ok) {
          const updated = await refreshRes.json();
          setMessages(Array.isArray(updated) ? updated : []);
        } else {
          // Remove optimistic message if refresh fails
          setMessages(prev => prev.filter(msg => !msg.isOptimistic));
        }
      } catch (refreshError) {
        console.warn('Failed to refresh messages:', refreshError);
        // Remove optimistic message if refresh fails
        setMessages(prev => prev.filter(msg => !msg.isOptimistic));
      }
      
      // Trigger inbox refresh
      if (triggerInboxRefresh) {
        triggerInboxRefresh();
      }
      
    } catch (err) {
      console.error('=== SEND MESSAGE ERROR ===');
      console.error('Error:', err);
      console.error('Error message:', err.message);
      
      setError(`Failed to send message: ${err.message}`);
      // Remove optimistic message and restore input
      setMessages(prev => prev.filter(msg => !msg.isOptimistic));
      setNewMessage(messageText);
    } finally {
      setIsSending(false);
    }
  };

  const formatMessageTime = dateString => {
    try {
      return new Date(dateString).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return '';
    }
  };

  const formatMessageDate = dateString => {
    try {
      const date = new Date(dateString);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      if (date.toDateString() === today.toDateString()) {
        return "Today";
      } else if (date.toDateString() === yesterday.toDateString()) {
        return "Yesterday";
      } else {
        return date.toLocaleDateString([], {
          month: 'short',
          day: 'numeric'
        });
      }
    } catch (e) {
      return '';
    }
  };

  // Get recipient name for header display
  const getRecipientName = () => {
    // Use provided recipientName prop first
    if (recipientName && recipientName !== 'Unknown User') return recipientName;
    
    // Use extracted recipient info from messages
    if (recipientInfo && recipientInfo.name) return recipientInfo.name;
    
    if (messages.length === 0) return recipientName || 'Loading...';
    
    // Find a message where the current user is not the sender to get the other person's name
    const otherPersonMessage = messages.find(msg => 
      msg.message_from !== actualCurrentUserId && (msg.sender_firstName || msg.sender_lastName)
    );
    if (otherPersonMessage) {
      return `${otherPersonMessage.sender_firstName || ''} ${otherPersonMessage.sender_lastName || ''}`.trim();
    }
    
    // Find a message where the current user is the sender to get the receiver's name
    const currentUserMessage = messages.find(msg => 
      msg.message_from === actualCurrentUserId && (msg.recipient_firstName || msg.recipient_lastName)
    );
    if (currentUserMessage) {
      return `${currentUserMessage.recipient_firstName || ''} ${currentUserMessage.recipient_lastName || ''}`.trim();
    }
    
    return recipientName || `User ${recipientId || 'Unknown'}`;
  };

  const getRoleName = (roleId) => {
    const role = roleId?.toString();
    return role === "1" ? "Admin" : role === "2" ? "Sub-Admin" : "Student";
  };

  const getRoleColor = (roleId) => {
    const role = roleId?.toString();
    return role === "1" 
      ? "bg-purple-100 text-purple-700" 
      : role === "2" 
      ? "bg-blue-100 text-blue-700"
      : "bg-green-100 text-green-700";
  };

  // Show error state if required props are missing
  if (!threadId || !actualCurrentUserId) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-md mx-auto">
        <div className="bg-red-100 p-4 rounded-full shadow-inner mb-3">
          <User size={24} className="text-red-500" />
        </div>
        <h3 className="text-base font-semibold text-red-700 mb-2">Unable to Load Conversation</h3>
        <p className="text-xs text-red-600 text-center mb-4 max-w-md">
          {error || 'Required information is missing. Please try going back to your inbox and selecting the conversation again.'}
        </p>
        <button
          onClick={() => setActiveSection('messages')}
          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 text-sm"
        >
          <ArrowLeft size={14} />
          Back to Messages
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
      <div className="max-w-4xl mx-auto p-3 sm:p-4">
        <div className="backdrop-blur-xl bg-white/80 border border-white/20 shadow-lg sm:shadow-xl rounded-xl sm:rounded-2xl overflow-hidden h-[75vh] flex flex-col">
          
          {/* Conversation Header */}
          <div className="bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-600 p-3 sm:p-4 flex items-center gap-3">
            <button
              onClick={() => setActiveSection('messages')}
              className="p-1.5 bg-white/20 rounded-lg hover:bg-white/30 transition-colors duration-200"
            >
              <ArrowLeft className="w-4 h-4 text-white" />
            </button>
            
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-lg flex items-center justify-center text-white font-bold text-sm">
              {getRecipientName()?.charAt(0) || "?"}
            </div>
            
            <div className="flex-1 min-w-0">
              <h2 className="text-base sm:text-lg font-bold text-white truncate">
                {getRecipientName()}
              </h2>
              <div className="flex items-center gap-2">
                {recipientInfo?.roleId && (
                  <span className="px-1.5 py-0.5 rounded-full text-xs font-semibold bg-white/20 text-white">
                    {getRoleName(recipientInfo.roleId)}
                  </span>
                )}
                <span className="text-white/80 text-xs">
                  {loading ? 'Loading...' : 
                   messages.length > 0 ? `${messages.length} messages` : 'No messages yet'}
                </span>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50/80 text-red-700 border-l-4 border-red-500 p-2 mx-3 mt-3 rounded-lg text-xs">
              <div className="flex justify-between items-center">
                <span className="flex-1">{error}</span>
                <button 
                  onClick={() => setError(null)}
                  className="text-red-500 hover:text-red-700 ml-2 flex-shrink-0"
                >
                  ×
                </button>
              </div>
            </div>
          )}

          {/* Messages Area */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3"
          >
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600 text-sm">Loading messages...</span>
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center py-6">
                <div className="p-2 bg-gradient-to-br from-gray-100 to-gray-50 rounded-lg inline-block mb-3">
                  <User className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-500 text-sm">No messages yet</p>
                <p className="text-gray-400 text-xs mt-1">Start the conversation!</p>
              </div>
            ) : (
              <>
                {messages.map((msg, index) => {
                  const isMyMessage = msg.message_from === actualCurrentUserId;
                  const showDate = index === 0 || 
                    formatMessageDate(msg.created_at) !== formatMessageDate(messages[index - 1]?.created_at);
                  
                  return (
                    <div key={msg.id || index}>
                      {/* Date separator */}
                      {showDate && (
                        <div className="flex justify-center my-3">
                          <span className="bg-gray-100 text-gray-500 text-xs px-2 py-1 rounded-full">
                            {formatMessageDate(msg.created_at)}
                          </span>
                        </div>
                      )}
                      
                      {/* Message bubble */}
                      <div className={`flex ${isMyMessage ? 'justify-end' : 'justify-start'} mb-2`}>
                        <div className="flex items-end gap-1.5 max-w-xs sm:max-w-md">
                          {!isMyMessage && (
                            <div className="w-6 h-6 sm:w-7 sm:h-7 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                              {(msg.sender_firstName || 'U')?.charAt(0)}
                            </div>
                          )}
                          
                          <div className={`relative px-3 py-2 rounded-xl ${
                            isMyMessage 
                              ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white'
                              : 'bg-white border border-gray-200 text-gray-800'
                          } ${msg.isOptimistic ? 'opacity-70' : ''}`}>
                            {!isMyMessage && (
                              <div className="text-xs font-semibold mb-0.5 opacity-70">
                                {msg.sender_firstName || 'Unknown'}
                              </div>
                            )}
                            <div className="text-sm whitespace-pre-wrap break-words">{msg.message}</div>
                            <div className={`text-xs mt-0.5 ${
                              isMyMessage ? 'text-white/70' : 'text-gray-500'
                            }`}>
                              {formatMessageTime(msg.created_at)}
                              {msg.isOptimistic && ' (sending...)'}
                            </div>
                          </div>
                          
                          {isMyMessage && (
                            <div className="w-6 h-6 sm:w-7 sm:h-7 bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                              S
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div id="messages-end" />
              </>
            )}
          </div>

          {/* Message Input */}
          <div className="border-t border-gray-200/50 p-3 sm:p-4 bg-white/50 backdrop-blur-sm">
            <div className="flex gap-2">
              <div className="flex-1">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={`Type your message to ${getRecipientName()}...`}
                  disabled={isSending || loading}
                  className="w-full bg-white/80 border border-gray-200 rounded-lg px-3 py-2 text-gray-800 focus:ring-2 focus:ring-blue-200 focus:border-blue-300 outline-none transition-all duration-300 resize-none disabled:opacity-50 text-sm"
                  rows="1"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                />
              </div>
              <button
                onClick={handleSend}
                disabled={!newMessage.trim() || isSending || loading}
                className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white p-2.5 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
              >
                {isSending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}